import { AgentDto, HandoffRuleRequestDto, ToolDto } from '@shared/services/apiService/apiService';
import {
  HandoffRuleEditorValue,
  MessageGroupValue,
  VariableExtractionFieldType,
  VariableExtractionRootSchema,
  VariableExtractionSchemaNode,
  VariableExtractionPlanFormValue,
} from '@features/Admin/Agent/handoffRules/domain/contracts';
import {
  asRecord,
  createEmptyHandoffRule,
  normalizeGroupedMessages,
  resolveRuleAdapter,
  serializeGroupedMessages,
} from '@features/Admin/Agent/handoffRules/domain/ruleRegistry';

export type HandoffRulesParseResult = {
  rules: HandoffRuleEditorValue[];
  canvasAgentIds: string[];
  nodePositions: Record<string, { x: number; y: number }>;
  hasError: boolean;
};

export type HandoffRulesDocument = {
  rules: HandoffRuleRequestDto[];
  canvasAgentIds: string[];
  nodePositions: Record<string, { x: number; y: number }>;
};

export type {
  HandoffRuleEditorValue,
  MessageGroupValue,
  VariableExtractionFieldType,
  VariableExtractionRootSchema,
  VariableExtractionSchemaNode,
  VariableExtractionPlanFormValue,
};

export type HandoffCanvasNode = {
  id: string;
  position: { x: number; y: number };
  data: {
    label: string;
    agentId: string;
    isDefault?: boolean;
    isEndCallEnabled?: boolean;
  };
};

export type HandoffCanvasEdge = {
  id: string;
  source: string;
  target: string;
  sourceHandle?: string;
  targetHandle?: string;
  data: {
    ruleId: string;
    label: string;
    /** Vertical path bias for parallel / reverse edges (px). */
    pathOffset?: number;
    /** Controls curve style in HandoffEdge. */
    routeKind?: 'forward' | 'back' | 'skip' | 'branch';
  };
};

export const HANDOFF_NODE_WIDTH = 180;
export const HANDOFF_NODE_HEIGHT = 72;

type EdgeHandles = {
  sourceHandle: string;
  targetHandle: string;
  pathOffset: number;
  routeKind: 'forward' | 'back' | 'skip' | 'branch';
};

/** Facing handles from relative node centers (supports A↔B on opposite sides). */
export const resolveHandoffEdgeHandles = (
  sourcePosition: { x: number; y: number },
  targetPosition: { x: number; y: number },
): { sourceHandle: string; targetHandle: string } => {
  const dx =
    targetPosition.x + HANDOFF_NODE_WIDTH / 2 - (sourcePosition.x + HANDOFF_NODE_WIDTH / 2);
  const dy =
    targetPosition.y + HANDOFF_NODE_HEIGHT / 2 - (sourcePosition.y + HANDOFF_NODE_HEIGHT / 2);

  if (dy > HANDOFF_NODE_HEIGHT * 0.55) {
    return { sourceHandle: 's-bottom', targetHandle: 't-top' };
  }
  if (dy < -HANDOFF_NODE_HEIGHT * 0.55) {
    return { sourceHandle: 's-top', targetHandle: 't-bottom' };
  }

  if (Math.abs(dx) >= Math.abs(dy)) {
    if (dx >= 0) return { sourceHandle: 's-right', targetHandle: 't-left' };
    return { sourceHandle: 's-left', targetHandle: 't-right' };
  }

  if (dy >= 0) return { sourceHandle: 's-bottom', targetHandle: 't-top' };
  return { sourceHandle: 's-top', targetHandle: 't-bottom' };
};

const nodeCenter = (position: { x: number; y: number }) => ({
  x: position.x + HANDOFF_NODE_WIDTH / 2,
  y: position.y + HANDOFF_NODE_HEIGHT / 2,
});

/** True when another node sits in the horizontal corridor between source and target. */
export const hasNodeBetweenOnCanvas = (
  sourceId: string,
  targetId: string,
  positionById: Map<string, { x: number; y: number }>,
): boolean => {
  const source = positionById.get(sourceId);
  const target = positionById.get(targetId);
  if (!source || !target) return false;

  const sourceMid = nodeCenter(source);
  const targetMid = nodeCenter(target);
  const xMin = Math.min(sourceMid.x, targetMid.x);
  const xMax = Math.max(sourceMid.x, targetMid.x);
  if (xMax - xMin < HANDOFF_NODE_WIDTH * 0.75) return false;

  const yMin = Math.min(source.y, target.y) - HANDOFF_NODE_HEIGHT * 0.35;
  const yMax = Math.max(source.y, target.y) + HANDOFF_NODE_HEIGHT * 1.35;

  for (const [id, position] of positionById) {
    if (id === sourceId || id === targetId) continue;
    const center = nodeCenter(position);
    const inX = center.x > xMin + 8 && center.x < xMax - 8;
    const inY = center.y >= yMin && center.y <= yMax;
    if (inX && inY) return true;
  }
  return false;
};

/**
 * Assign readable handles for chains, cycles, and branches.
 * Forward flow stays left→right; back-edges curve under the row; skips arc above.
 */
export const assignHandoffEdgeHandles = (
  sourceId: string,
  targetId: string,
  positionById: Map<string, { x: number; y: number }>,
  options: {
    reverseExists: boolean;
    parallelIndex?: number;
    sourceLayer?: number;
    targetLayer?: number;
  } = { reverseExists: false },
): EdgeHandles => {
  const source = positionById.get(sourceId) ?? { x: 0, y: 0 };
  const target = positionById.get(targetId) ?? { x: 0, y: 0 };
  const sourceMid = nodeCenter(source);
  const targetMid = nodeCenter(target);
  const dx = targetMid.x - sourceMid.x;
  const dy = targetMid.y - sourceMid.y;
  const parallelIndex = options.parallelIndex ?? 0;
  const parallelOffset = parallelIndex * 36;
  const sameRow = Math.abs(dy) < HANDOFF_NODE_HEIGHT * 0.9;
  const sourceLayer = options.sourceLayer;
  const targetLayer = options.targetLayer;
  const isLayerBackEdge =
    typeof sourceLayer === 'number' && typeof targetLayer === 'number' && targetLayer < sourceLayer;

  // Explicit cycle / back-edge: curve under the main chain.
  if (isLayerBackEdge || (dx < -HANDOFF_NODE_WIDTH * 0.5 && dy <= HANDOFF_NODE_HEIGHT)) {
    return {
      sourceHandle: 's-bottom',
      targetHandle: 't-bottom',
      pathOffset: 48 + parallelOffset,
      routeKind: 'back',
    };
  }

  const isShortBidirectionalReverse =
    options.reverseExists &&
    sameRow &&
    Math.hypot(dx, dy) < HANDOFF_NODE_WIDTH * 3.25 &&
    (sourceMid.x > targetMid.x || (Math.abs(dx) < 4 && sourceId > targetId));

  if (isShortBidirectionalReverse) {
    return {
      sourceHandle: 's-bottom',
      targetHandle: 't-bottom',
      pathOffset: 28 + parallelOffset,
      routeKind: 'back',
    };
  }

  const blocked = hasNodeBetweenOnCanvas(sourceId, targetId, positionById);
  if (blocked && sameRow) {
    return {
      sourceHandle: 's-top',
      targetHandle: 't-top',
      pathOffset: -28 - parallelOffset,
      routeKind: 'skip',
    };
  }

  // Branch to a node clearly below the source.
  if (dy > HANDOFF_NODE_HEIGHT * 0.7) {
    return {
      sourceHandle: 's-bottom',
      targetHandle: 't-top',
      pathOffset: parallelOffset,
      routeKind: 'branch',
    };
  }

  const facing = resolveHandoffEdgeHandles(source, target);
  return { ...facing, pathOffset: parallelOffset, routeKind: 'forward' };
};

export const normalizeHandoffRules = (input: unknown): HandoffRuleEditorValue[] => {
  const rules = Array.isArray(input) ? input : [];
  return rules.map((rule) => {
    const raw = asRecord(rule);
    return resolveRuleAdapter(raw).normalize(raw);
  });
};

export const parseHandoffRulesValue = (value: string | unknown): HandoffRulesParseResult => {
  if (value === '' || value === null || value === undefined) {
    return { rules: [], canvasAgentIds: [], nodePositions: {}, hasError: false };
  }

  const parseNodePositions = (input: unknown): Record<string, { x: number; y: number }> => {
    const source = asRecord(input);
    return Object.entries(source).reduce<Record<string, { x: number; y: number }>>(
      (acc, [agentId, position]) => {
        if (!agentId) return acc;
        const parsedPosition = asRecord(position);
        const x = parsedPosition.x;
        const y = parsedPosition.y;
        if (typeof x !== 'number' || Number.isNaN(x)) return acc;
        if (typeof y !== 'number' || Number.isNaN(y)) return acc;
        acc[agentId] = { x, y };
        return acc;
      },
      {},
    );
  };

  const parseDocumentShape = (input: unknown): HandoffRulesParseResult => {
    if (Array.isArray(input)) {
      const rules = normalizeHandoffRules(input);
      return {
        rules,
        canvasAgentIds: getReferencedHandoffAgentIds(rules),
        nodePositions: {},
        hasError: false,
      };
    }

    const payload = asRecord(input);
    const rawRules = Array.isArray(payload.rules) ? payload.rules : [];
    const rules = normalizeHandoffRules(rawRules);
    const referencedAgentIds = getReferencedHandoffAgentIds(rules);
    const storedCanvasAgentIds = Array.isArray(payload.canvasAgentIds)
      ? payload.canvasAgentIds.filter((agentId): agentId is string => typeof agentId === 'string')
      : [];
    const canvasAgentIds = Array.from(
      new Set([...storedCanvasAgentIds, ...referencedAgentIds].filter(Boolean)),
    ) as string[];
    return {
      rules,
      canvasAgentIds,
      nodePositions: parseNodePositions(payload.nodePositions),
      hasError: false,
    };
  };

  if (typeof value === 'string') {
    try {
      return parseDocumentShape(JSON.parse(value));
    } catch {
      return { rules: [], canvasAgentIds: [], nodePositions: {}, hasError: true };
    }
  }

  return parseDocumentShape(value);
};

export const serializeHandoffRules = (rules: HandoffRuleEditorValue[]): HandoffRuleRequestDto[] => {
  return rules.map((rule) => {
    const adapter = resolveRuleAdapter(rule.raw);
    return adapter.serialize(rule);
  });
};

export const serializeHandoffRulesDocument = (
  rules: HandoffRuleEditorValue[],
  canvasAgentIds: string[],
  nodePositions: Record<string, { x: number; y: number }>,
): HandoffRulesDocument => {
  const serializedRules = serializeHandoffRules(rules);
  const referencedAgentIds = getReferencedHandoffAgentIds(rules);
  return {
    rules: serializedRules,
    canvasAgentIds: Array.from(
      new Set([...canvasAgentIds, ...referencedAgentIds].filter(Boolean)),
    ) as string[],
    nodePositions,
  };
};

/** Agents referenced by handoff routes or session boundary plans. */
export const getReferencedHandoffAgentIds = (rules: HandoffRuleEditorValue[]): string[] =>
  Array.from(
    new Set(
      rules
        .flatMap((rule) => [
          rule.fromAgentId,
          rule.toAgentId,
          rule.sessionBoundaryPlan?.startAgentId,
          ...(rule.sessionBoundaryPlan?.endingAgentIds ?? []),
        ])
        .filter((agentId): agentId is string => Boolean(agentId)),
    ),
  );

/** Keep only agents referenced by rules (and their positions). Used on save. */
export const pruneHandoffCanvasToReferencedAgents = (
  rules: HandoffRuleEditorValue[],
  nodePositions: Record<string, { x: number; y: number }>,
): {
  canvasAgentIds: string[];
  nodePositions: Record<string, { x: number; y: number }>;
} => {
  const canvasAgentIds = getReferencedHandoffAgentIds(rules);
  return {
    canvasAgentIds,
    nodePositions: Object.fromEntries(
      Object.entries(nodePositions).filter(([agentId]) => canvasAgentIds.includes(agentId)),
    ),
  };
};

export { createEmptyHandoffRule, normalizeGroupedMessages, serializeGroupedMessages };

export const buildHandoffCanvasGraph = (
  rules: HandoffRuleEditorValue[],
  agents: AgentDto[],
  nodePositions?: Record<string, { x: number; y: number }>,
): { nodes: HandoffCanvasNode[]; edges: HandoffCanvasEdge[] } => {
  const agentMap = new Map(agents.map((agent) => [agent.id, agent]));
  const nodeSet = new Set<string>();

  // Always include explicitly available canvas agents, even if no rule references them yet.
  for (const agent of agents) {
    nodeSet.add(agent.id);
  }

  for (const rule of rules) {
    if (rule.fromAgentId) nodeSet.add(rule.fromAgentId);
    if (rule.toAgentId) nodeSet.add(rule.toAgentId);
    if (rule.sessionBoundaryPlan?.startAgentId) {
      nodeSet.add(rule.sessionBoundaryPlan.startAgentId);
    }
    for (const endingAgentId of rule.sessionBoundaryPlan?.endingAgentIds ?? []) {
      if (endingAgentId) nodeSet.add(endingAgentId);
    }
  }

  const nodeIds = Array.from(nodeSet);

  const autoPositions = (() => {
    // Layered left-to-right layout. Main chain stays on one row; back-edges curve
    // underneath; pure branch leaves sit slightly below their parent.
    const edges = rules
      .filter((rule) => Boolean(rule.fromAgentId && rule.toAgentId))
      .map((rule) => ({ from: rule.fromAgentId, to: rule.toAgentId }));

    const indegree = new Map<string, number>(nodeIds.map((id) => [id, 0]));
    const outgoing = new Map<string, string[]>(nodeIds.map((id) => [id, []]));
    const incoming = new Map<string, string[]>(nodeIds.map((id) => [id, []]));

    for (const { from, to } of edges) {
      if (!outgoing.has(from)) outgoing.set(from, []);
      if (!incoming.has(to)) incoming.set(to, []);
      outgoing.get(from)!.push(to);
      incoming.get(to)!.push(from);
      indegree.set(to, (indegree.get(to) ?? 0) + 1);
    }

    const layer = new Map<string, number>(nodeIds.map((id) => [id, 0]));
    const workingIndegree = new Map(indegree);
    const remaining = new Set(nodeIds);

    const sortByName = (a: string, b: string) => {
      const aName = agentMap.get(a)?.name ?? a;
      const bName = agentMap.get(b)?.name ?? b;
      return aName.localeCompare(bName);
    };

    const q: string[] = nodeIds
      .filter((id) => (workingIndegree.get(id) ?? 0) === 0)
      .sort(sortByName);

    // Cycles can leave no roots — seed a stable start so the main chain still layers L→R.
    const pickSeed = () =>
      Array.from(remaining).sort((a, b) => {
        const indegDiff = (indegree.get(a) ?? 0) - (indegree.get(b) ?? 0);
        if (indegDiff !== 0) return indegDiff;
        return sortByName(a, b);
      })[0];

    while (remaining.size > 0) {
      if (q.length === 0) {
        const seed = pickSeed();
        if (!seed) break;
        q.push(seed);
      }
      const current = q.shift()!;
      if (!remaining.has(current)) continue;
      remaining.delete(current);

      for (const next of outgoing.get(current) ?? []) {
        // Ignore back-edges to already-placed nodes when assigning layers.
        if (!remaining.has(next)) continue;
        layer.set(next, Math.max(layer.get(next) ?? 0, (layer.get(current) ?? 0) + 1));
        workingIndegree.set(next, (workingIndegree.get(next) ?? 0) - 1);
        if ((workingIndegree.get(next) ?? 0) <= 0) {
          q.push(next);
        }
      }
    }

    // Bidirectional pairs in the same layer: nudge one node forward.
    const pairSeen = new Set<string>();
    for (const { from, to } of edges) {
      if (!from || !to) continue;
      const pairKey = [from, to].sort().join('::');
      if (pairSeen.has(pairKey)) continue;
      pairSeen.add(pairKey);
      const hasReverse = edges.some((edge) => edge.from === to && edge.to === from);
      if (!hasReverse) continue;
      if ((layer.get(from) ?? 0) !== (layer.get(to) ?? 0)) continue;
      const [first, second] = [from, to].sort((a, b) => {
        const aName = agentMap.get(a)?.name ?? a;
        const bName = agentMap.get(b)?.name ?? b;
        return aName.localeCompare(bName);
      });
      layer.set(second, (layer.get(first) ?? 0) + 1);
    }

    const byLayer = new Map<number, string[]>();
    for (const id of nodeIds) {
      const l = layer.get(id) ?? 0;
      const list = byLayer.get(l) ?? [];
      list.push(id);
      byLayer.set(l, list);
    }

    const layerKeys = Array.from(byLayer.keys()).sort((a, b) => a - b);
    const orderIndex = new Map<string, number>();

    for (const l of layerKeys) {
      const ids = byLayer.get(l) ?? [];
      ids.sort((a, b) => {
        const aNeighbors = [...(incoming.get(a) ?? []), ...(outgoing.get(a) ?? [])];
        const bNeighbors = [...(incoming.get(b) ?? []), ...(outgoing.get(b) ?? [])];
        const aScore =
          aNeighbors.reduce((sum, id) => sum + (orderIndex.get(id) ?? 0), 0) /
          Math.max(aNeighbors.length, 1);
        const bScore =
          bNeighbors.reduce((sum, id) => sum + (orderIndex.get(id) ?? 0), 0) /
          Math.max(bNeighbors.length, 1);
        if (aScore !== bScore) return aScore - bScore;
        const aName = agentMap.get(a)?.name ?? a;
        const bName = agentMap.get(b)?.name ?? b;
        return aName.localeCompare(bName);
      });
      ids.forEach((id, index) => orderIndex.set(id, index));
      byLayer.set(l, ids);
    }

    const positions: Record<string, { x: number; y: number }> = {};
    const maxLayerSize = Math.max(...layerKeys.map((l) => (byLayer.get(l) ?? []).length), 1);
    const hasBackEdge = edges.some(({ from, to }) => (layer.get(to) ?? 0) < (layer.get(from) ?? 0));
    const hasSkipEdge = edges.some(
      ({ from, to }) => Math.abs((layer.get(from) ?? 0) - (layer.get(to) ?? 0)) > 1,
    );
    // Keep the main flow on one horizontal row whenever possible so edges read as a chain.
    const useSingleRow = maxLayerSize === 1 || (maxLayerSize <= 2 && (hasBackEdge || hasSkipEdge));

    const xSpacing = useSingleRow ? 320 : 300;
    const ySpacing = 180;
    const xStart = 100;
    const yStart = 160;

    for (const l of layerKeys) {
      const ids = byLayer.get(l) ?? [];
      for (let i = 0; i < ids.length; i += 1) {
        const id = ids[i];
        if (useSingleRow) {
          // Primary node of the layer on the main row; extras branch below.
          positions[id] = {
            x: xStart + l * xSpacing + (i > 0 ? 40 : 0),
            y: yStart + i * ySpacing,
          };
        } else {
          const layerHeight = Math.max(ids.length - 1, 0) * ySpacing;
          positions[id] = {
            x: xStart + l * xSpacing,
            y: yStart + i * ySpacing - layerHeight / 2,
          };
        }
      }
    }

    // Pure leaf with a branching parent: park it to the right on the main row
    // (or one step below if the right slot is taken).
    for (const { from, to } of edges) {
      if (!from || !to) continue;
      const parents = incoming.get(to) ?? [];
      const children = outgoing.get(to) ?? [];
      if (parents.length !== 1 || children.length > 0) continue;
      if ((outgoing.get(from) ?? []).length < 2) continue;
      const parentPos = positions[from];
      if (!parentPos) continue;
      const siblingOnMainRow = Object.entries(positions).some(
        ([id, pos]) =>
          id !== to &&
          Math.abs(pos.y - parentPos.y) < 20 &&
          pos.x > parentPos.x + xSpacing * 0.5 &&
          pos.x < parentPos.x + xSpacing * 1.5,
      );
      positions[to] = siblingOnMainRow
        ? { x: parentPos.x + xSpacing * 0.35, y: parentPos.y + ySpacing }
        : { x: parentPos.x + xSpacing, y: parentPos.y };
    }

    return { positions, layer };
  })();

  const endingAgentIds = new Set(
    rules.flatMap((rule) => rule.sessionBoundaryPlan?.endingAgentIds ?? []).filter(Boolean),
  );
  const startAgentIds = new Set(
    rules
      .map((rule) => rule.sessionBoundaryPlan?.startAgentId)
      .filter((agentId): agentId is string => Boolean(agentId)),
  );

  const nodes: HandoffCanvasNode[] = nodeIds.map((agentId, index) => {
    const agent = agentMap.get(agentId);
    const hasEndCallTool = Boolean(
      (agent as (AgentDto & { tools?: ToolDto[] }) | undefined)?.tools?.some(
        (tool) => tool.type === 'end-call',
      ),
    );
    const isSquadDefault = Boolean(
      (agent as (AgentDto & { isDefault?: boolean }) | undefined)?.isDefault,
    );

    return {
      id: agentId,
      position: nodePositions?.[agentId] ??
        autoPositions.positions[agentId] ?? { x: 80 + index * 40, y: 80 + index * 30 },
      data: {
        label: agent?.name || agentId,
        agentId,
        // Align canvas chips with squad default + handoff session boundary UI.
        isDefault: isSquadDefault || startAgentIds.has(agentId),
        isEndCallEnabled: hasEndCallTool || endingAgentIds.has(agentId),
      },
    };
  });

  const positionById = new Map(nodes.map((node) => [node.id, node.position]));
  const layerById = autoPositions.layer;
  const connectedPairs = new Set(
    rules
      .filter((rule) => Boolean(rule.fromAgentId && rule.toAgentId))
      .map((rule) => `${rule.fromAgentId}::${rule.toAgentId}`),
  );
  const parallelIndexByRuleId = new Map<string, number>();
  const parallelSeen = new Map<string, number>();
  for (const rule of rules) {
    if (!rule.fromAgentId || !rule.toAgentId) continue;
    const key = `${rule.fromAgentId}::${rule.toAgentId}`;
    const index = parallelSeen.get(key) ?? 0;
    parallelIndexByRuleId.set(rule.id, index);
    parallelSeen.set(key, index + 1);
  }

  const edges: HandoffCanvasEdge[] = rules
    .filter((rule) => Boolean(rule.fromAgentId && rule.toAgentId))
    .map((rule) => {
      const reverseExists = connectedPairs.has(`${rule.toAgentId}::${rule.fromAgentId}`);
      const handles = assignHandoffEdgeHandles(rule.fromAgentId, rule.toAgentId, positionById, {
        reverseExists,
        parallelIndex: parallelIndexByRuleId.get(rule.id) ?? 0,
        sourceLayer: layerById.get(rule.fromAgentId),
        targetLayer: layerById.get(rule.toAgentId),
      });

      return {
        id: `edge-${rule.id}-${rule.fromAgentId}-${rule.toAgentId}`,
        source: rule.fromAgentId,
        target: rule.toAgentId,
        sourceHandle: handles.sourceHandle,
        targetHandle: handles.targetHandle,
        data: {
          ruleId: rule.id,
          label: rule.name || 'handoff',
          pathOffset: handles.pathOffset,
          routeKind: handles.routeKind,
        },
      };
    });

  return { nodes, edges };
};
